import { motion } from "framer-motion";
import { useLanguage } from "@/lib/language";

const EASE = [0.16, 1, 0.3, 1];

const SPAN_PATTERNS = {
  2: ["lg:col-span-7", "lg:col-span-5"],
  3: ["lg:col-span-5", "lg:col-span-3", "lg:col-span-4"],
};

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 48, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 1.2, delay, ease: EASE },
});

export const SpaceSection = ({ space, index }) => {
  const reverse = index % 2 === 1;
  const images = reverse ? [...space.images].reverse() : space.images;
  const spans = SPAN_PATTERNS[images.length];

  return (
    <section
      id={space.id}
      data-testid="space-card-section"
      className={`py-20 lg:py-28 ${index % 2 === 1 ? "bg-beige" : "bg-cream"}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          {...reveal()}
          className="flex items-baseline gap-5 border-b border-border-subtle pb-8"
        >
          <span className="font-serif italic text-2xl lg:text-3xl text-gold">
            {space.num}
          </span>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-ink">
            {space.title}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 mt-10 lg:mt-12 items-start">
          <motion.p
            {...reveal(0.08)}
            className="lg:col-span-5 font-serif text-xl lg:text-2xl font-light leading-relaxed text-ink/90"
          >
            {space.lead}
          </motion.p>
          <div className="lg:col-span-7">
            <motion.ul
              {...reveal(0.16)}
              className="grid sm:grid-cols-2 gap-x-8 gap-y-4"
            >
              {space.features.map((f) => (
                <li
                  key={f}
                  className="flex gap-4 text-sm leading-relaxed text-stone-600"
                >
                  <span className="text-gold mt-0.5">—</span>
                  {f}
                </li>
              ))}
            </motion.ul>
            {space.note && (
              <motion.p
                {...reveal(0.24)}
                className="mt-6 flex items-center gap-2 text-sm italic text-stone-500"
                data-testid="spa-note"
              >
                <span className="text-gold not-italic font-medium">*</span>
                {space.note}
              </motion.p>
            )}
          </div>
        </div>

        <div
          className={`mt-10 lg:mt-14 grid gap-4 lg:gap-6 ${
            images.length === 1
              ? "grid-cols-1"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-12"
          }`}
        >
          {images.map((img, i) => (
            <motion.div
              key={img.src}
              data-testid="space-gallery-image"
              initial={{ opacity: 0, y: 56, scale: 0.985, filter: "blur(12px)" }}
              whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1.3, delay: 0.1 * i, ease: EASE }}
              className={`group overflow-hidden ${
                images.length === 1
                  ? "w-full"
                  : `h-64 sm:h-72 lg:h-[440px] ${spans ? spans[i] : ""}`
              }`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className={`w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.06] ${
                  images.length === 1
                    ? "aspect-[21/9] lg:aspect-auto lg:h-[520px]"
                    : ""
                }`}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SpaceSection;
